import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Person, CalendarCheck, DoorOpen } from "react-bootstrap-icons";
import "../../assets/css/vendors/HotelComponents/booking-detail.css";
import VendorLayout from "../../layout/HotelVendorLayout/VendorLayout";

const getBadgeClass = (status) => {
  if (!status) return "bdk-badge-default";
  const s = status.toLowerCase();
  if (s === "confirmed" || s === "paid" || s === "available") return "bdk-badge-confirmed";
  if (s === "pending" || s === "reserved") return "bdk-badge-pending";
  if (s === "cancelled" || s === "unpaid") return "bdk-badge-cancelled";
  if (s === "completed") return "bdk-badge-completed";

  if (s === "partial") return "bdk-badge-partial";
  if (s === "refunded") return "bdk-badge-refunded";
  if (s === "booked" || s === "occupied") return "bdk-badge-booked";
  if (s === "maintenance") return "bdk-badge-maintenance";
  if (s === "inactive") return "bdk-badge-inactive";
  return "bdk-badge-default";
};

const formatDate = (d) => {
  if (!d) return "N/A";
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
};

const capitalize = (s) => {
  if (!s) return "N/A";
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const Field = ({ label, value }) => (
  <div className="bdk-field">
    <span className="bdk-label">{label}</span>
    <span className="bdk-value">{value || "N/A"}</span>
  </div>
);

const BadgeField = ({ label, value }) => (
  <div className="bdk-field">
    <span className="bdk-label">{label}</span>
    <span className={`bdk-badge ${getBadgeClass(value)}`}>
      {value ? capitalize(value) : "N/A"}
    </span>
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="bdk-section-header">
      <Icon size={15} />
      <h6>{title}</h6>
    </div>
    <div className="bdk-section-content">
      <div className="bdk-grid">{children}</div>
    </div>
  </div>
);

const BookingDetail = ({ booking: propBooking, onBack: propOnBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const booking = propBooking || location.state?.booking;
  
  const handleBack = () => {
    if (propOnBack) {
      propOnBack();
    } else {
      navigate("/vendor/hotel-bookings");
    }
  };

  if (!booking) {
    navigate("/vendor/hotel-bookings");
    return null;
  }

  const getBookedRoom = () => {
    const allRooms = booking.jjvendor?.jjhotelregistration?.jjrooms || [];
    if (booking.jjroom_id) {
      const bookedRoom = allRooms.find(room => room.id === booking.jjroom_id);
      if (bookedRoom) return bookedRoom;
    }
    return allRooms[0] || {};
  };
  const customer = booking.jjcustomer || {};
  const vendor = booking.jjvendor || {};
  const hotel = vendor.jjhotelregistration || {};
  const room = getBookedRoom();  
  const vendorName = vendor.name || hotel.hotel_name || "N/A";

  return (
    <VendorLayout>
    <div className="bdk-page">
      <div className="bdk-white-wrapper">
        <div className="bdk-inner">
          <button className="bdk-back-btn" onClick={handleBack}>
            <ArrowLeft size={16} /> Back to Bookings
          </button>

          <h2 className="bdk-page-title">Booking Detail</h2>
          <p className="bdk-page-subtitle">
            Full detail — customer, booking & room information
          </p>

          <div className="bdk-status-bar">
            <BadgeField label="Booking Status" value={booking.booking_status} />
            <BadgeField label="Payment Status" value={booking.payment_status} />
            <div className="bdk-field">
              <span className="bdk-label">Booking Type</span>
              <span className="bdk-value">{capitalize(booking.booking_type)}</span>
            </div>
          </div>

          <Section icon={Person} title="Customer Information">
            <Field label="Full Name" value={customer.cus_username} />
            <Field label="Email" value={customer.cus_email} />
            <Field label="Phone" value={booking.phone_no || customer.cus_phone} />
            <Field label="City" value={booking.cus_city || customer.cus_city} />
          </Section>

          <Section icon={CalendarCheck} title="Booking Information">
            <Field label="Vendor Name" value={vendorName} />
            <Field label="Check-In" value={formatDate(booking.checkIn)} />
            <Field label="Check-Out" value={formatDate(booking.checkOut)} />
            <Field label="No. of Guests" value={booking.number_of_guests} />
             <Field
             label="Total Amount"
             value={booking.total_amount ? `PKR ${Number(booking.total_amount).toLocaleString()}` : "N/A"} />
             <Field
             label="Reservation Amount"
             value={booking.reservation_amount ? `PKR ${Number(booking.reservation_amount).toLocaleString()}` : "N/A"}
              />
             <Field
             label="Remaining Amount"
             value={booking.remaining_amount ? `PKR ${Number(booking.remaining_amount).toLocaleString()}` : "N/A"}
             />
          </Section>

          <Section icon={DoorOpen} title="Room Information">
            <Field label="Hotel Name" value={hotel.hotel_name} />
            <Field label="Room Type" value={room.room_type} />
            <Field label="Room Number" value={room.room_number} />
            <Field
              label="Price / Night"
              value={room.price ? `PKR ${Number(room.price).toLocaleString()}` : "N/A"}
            />
            <Field label="Capacity" value={room.capacity} />
            <Field label="Floor" value={room.floor} />
            <BadgeField label="Room Status" value={room.status} />
            {room.description && (
              <div className="bdk-field-full">
                <span className="bdk-label">Description</span>
                <span className="bdk-value">{room.description}</span>
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
    </VendorLayout>
  );
};

export default BookingDetail;